import config from 'constants/config'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { getDestinationImage } from 'lib/search/searchUtils'
import { getImageUrl } from 'lib/image/imageUtils'

type SearchItemLike = App.SearchItem | undefined

interface Params {
  searchItem: SearchItemLike
  destinationId?: string
  destinationName?: string
  totalCount: number
  specificPlaces?: Array<App.SpecificPlace> | undefined
  destinationImageId?: string
}

interface Result {
  title: string
  description: string
  imageId?: string
  imageUrl?: string
  imageAlt: string
}

const numberFormatter = new Intl.NumberFormat()

function normaliseSecondaryText(secondaryText?: string): string | undefined {
  if (!secondaryText) {
    return undefined
  }
  return secondaryText
    .split('·')
    .map(part => part.trim())
    .filter(Boolean)
    .join(', ')
}

function findSpecificPlaceImage(places?: Array<App.SpecificPlace>): { imageId?: string, imageUrl?: string } {
  if (!places?.length) {
    return {}
  }

  const candidate = places.find(place => !!place.image)?.image
  if (!candidate) {
    return {}
  }

  if (candidate.startsWith('http')) {
    return { imageUrl: candidate }
  }

  return { imageId: candidate }
}

function isSearchDestination(searchItem: SearchItemLike): searchItem is App.SearchDestination {
  return searchItem?.searchType === 'destination'
}

function isSearchProperty(searchItem: SearchItemLike): searchItem is App.SearchProperty {
  return searchItem?.searchType === 'property'
}

export function getAccommodationSearchHeadMeta({
  searchItem,
  destinationId,
  destinationName,
  totalCount,
  specificPlaces,
  destinationImageId,
}: Params): Result {
  const searchDestination = isSearchDestination(searchItem) ? searchItem : undefined
  const isPropertySearch = isSearchProperty(searchItem)

  const mainText = searchItem?.format?.mainText?.trim()
  const secondaryText = normaliseSecondaryText(searchItem?.format?.secondaryText?.trim())

  const isAnywhereSearch = (destinationId && destinationId === ANYWHERE_PLACE_ID) ||
    (mainText && mainText.toLowerCase() === ANYWHERE_NAME.toLowerCase())

  const destinationType = searchDestination?.destinationType

  const count = Number.isFinite(totalCount) ? Math.max(totalCount, 0) : 0
  const formattedCount = numberFormatter.format(count)
  const hotelWord = count === 1 ? 'hotel' : 'hotels'

  const searchItemImageId = searchItem?.format?.imageId || searchItem?.imageId
  const mappedDestinationImageId = destinationId ? getDestinationImage(destinationId) : undefined
  const specificPlaceImage = findSpecificPlaceImage(specificPlaces)

  if (isPropertySearch) {
    const propertyName = mainText || destinationName || config.title
    const propertyLocation = secondaryText || destinationName
    const propertyDescription = propertyLocation
      ? `Discover ${propertyName} in ${propertyLocation} with ${config.title}.`
      : `Discover ${propertyName} with ${config.title}.`

    const imageId = searchItemImageId || destinationImageId || specificPlaceImage.imageId || mappedDestinationImageId
    let imageUrl = specificPlaceImage.imageUrl

    if (!imageUrl && imageId) {
      imageUrl = getImageUrl(imageId, OG_IMAGE_OPTIONS)
    }

    const fallbackImageId = imageId ?? config.head.brandOpenGraphImageId
    const fallbackImageUrl = imageUrl ?? getImageUrl(config.head.brandOpenGraphImageId, OG_IMAGE_OPTIONS)

    return {
      title: propertyName,
      description: propertyDescription,
      imageId: fallbackImageId,
      imageUrl: fallbackImageUrl,
      imageAlt: propertyName,
    }
  }

  let locationLabel = mainText || destinationName || config.title
  let descriptiveLocation = locationLabel
  let locationPhrase: string

  if (isAnywhereSearch) {
    locationLabel = config.title
    descriptiveLocation = 'around the world'
    locationPhrase = `on ${config.title}`
  } else if (destinationType === 'country' || !secondaryText || secondaryText.localeCompare(locationLabel, undefined, { sensitivity: 'base' }) === 0) {
    locationPhrase = `in ${locationLabel}`
  } else {
    const combinedLocation = `${locationLabel}, ${secondaryText}`
    locationLabel = combinedLocation
    descriptiveLocation = combinedLocation
    locationPhrase = `in ${combinedLocation}`
  }

  if (!isAnywhereSearch && destinationType === 'country') {
    descriptiveLocation = locationLabel
  }

  const description = `Explore ${formattedCount} ${hotelWord} in ${descriptiveLocation} with ${config.title}.`

  const imageId = searchItemImageId || destinationImageId || specificPlaceImage.imageId || mappedDestinationImageId
  let imageUrl = specificPlaceImage.imageUrl

  if (!imageUrl && imageId) {
    imageUrl = getImageUrl(imageId, OG_IMAGE_OPTIONS)
  }

  const imageAlt = isAnywhereSearch
    ? `${config.title} hotel search results`
    : `${descriptiveLocation} ${hotelWord}`

  return {
    title: `Browse the best ${hotelWord} ${locationPhrase}`,
    description,
    imageId: imageId ?? config.head.brandOpenGraphImageId,
    imageUrl: imageUrl ?? getImageUrl(config.head.brandOpenGraphImageId, OG_IMAGE_OPTIONS),
    imageAlt,
  }
}
const OG_IMAGE_OPTIONS = { width: 1200, aspectRatio: '16:9', format: 'jpeg' as const }
